var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import MuiIconButton from '@material-ui/core/IconButton';
import { emphasize, withStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
const outlineFocused = (theme, color) => ({
    borderColor: theme.palette[color].dark,
    color: theme.palette[color].dark,
});
const containedFocused = (theme, color) => ({
    backgroundColor: theme.palette[color].dark,
    color: theme.palette.common.white,
});
const filledFocused = (theme, color) => ({
    backgroundColor: emphasize(theme.palette[color].light, 0.1),
    color: theme.palette[color].main,
});
const disabledOutlined = (theme, color) => ({
    borderColor: theme.palette[color].main,
    color: theme.palette[color].main,
});
const disabledContained = (theme, color) => ({
    backgroundColor: theme.palette[color].main,
    color: theme.palette.common.white,
});
const disabledFilled = (theme, color) => ({
    backgroundColor: theme.palette[color].light,
    color: theme.palette[color].main,
});
const outlined = (theme, color) => ({
    borderWidth: '1.5px',
    borderStyle: 'solid',
    borderColor: theme.palette[color].main,
    color: theme.palette[color].main,
});
const contained = (theme, color) => ({
    backgroundColor: theme.palette[color].main,
    color: theme.palette.common.white,
});
const filled = (theme, color) => ({
    backgroundColor: theme.palette[color].light,
    color: theme.palette[color].main,
});
const IconButton = withStyles((theme) => ({
    root: {
        '&:hover': {
            '&$outlinedPrimary': outlineFocused(theme, 'primary'),
            '&$outlinedSecondary': outlineFocused(theme, 'secondary'),
            '&$containedPrimary': containedFocused(theme, 'primary'),
            '&$containedSecondary': containedFocused(theme, 'secondary'),
            '&$filledPrimary': filledFocused(theme, 'primary'),
            '&$filledSecondary': filledFocused(theme, 'secondary'),
        },
    },
    disabled: {
        opacity: 0.24,
        '&$outlinedPrimary': disabledOutlined(theme, 'primary'),
        '&$outlinedSecondary': disabledOutlined(theme, 'secondary'),
        '&$containedPrimary': disabledContained(theme, 'primary'),
        '&$containedSecondary': disabledContained(theme, 'secondary'),
        '&$filledPrimary': disabledFilled(theme, 'primary'),
        '&$filledSecondary': disabledFilled(theme, 'secondary'),
    },
    outlinedPrimary: outlined(theme, 'primary'),
    outlinedSecondary: outlined(theme, 'secondary'),
    containedPrimary: contained(theme, 'primary'),
    containedSecondary: contained(theme, 'secondary'),
    filledPrimary: filled(theme, 'primary'),
    filledSecondary: filled(theme, 'secondary'),
    rounded: {
        borderRadius: theme.spacing(3),
    },
    circle: {
        borderRadius: '50%',
    },
}), { name: 'TpIconButton' })((_a) => {
    var { variant = 'default', color, classes, shape = 'circle' } = _a, props = __rest(_a, ["variant", "color", "classes", "shape"]);
    const _b = classes, { containedPrimary, containedSecondary, outlinedPrimary, outlinedSecondary, filledPrimary, filledSecondary, rounded, circle } = _b, otherClasses = __rest(_b, ["containedPrimary", "containedSecondary", "outlinedPrimary", "outlinedSecondary", "filledPrimary", "filledSecondary", "rounded", "circle"]);
    const className = clsx(props.className, {
        [containedPrimary]: variant === 'contained' && color === 'primary',
        [containedSecondary]: variant === 'contained' && color === 'secondary',
        [outlinedPrimary]: variant === 'outlined' && color === 'primary',
        [outlinedSecondary]: variant === 'outlined' && color === 'secondary',
        [filledPrimary]: variant === 'filled' && color === 'primary',
        [filledSecondary]: variant === 'filled' && color === 'secondary',
        [rounded]: shape === 'rounded',
        [circle]: shape === 'circle',
    });
    return (React.createElement(MuiIconButton, Object.assign({}, props, { className: className, classes: otherClasses, color: color })));
});
/**
 * A customized IconButton with support for:
 *
 * * `contained`, `outlined` and `filled` variants
 * * `rounded` and `circle` shapes.
 *
 * @component
 * @param {IconButtonProps} props
 * */
export default IconButton;
